import * as React from 'react';
import { cn } from '../lib/utils';

function splitTableSections(children) {
  const items = React.Children.toArray(children);
  let header = null;
  let body = null;
  const rest = [];

  items.forEach((child) => {
    const name = child?.type?.displayName || child?.type?.name;
    if (name === 'TableHeader') header = child;
    else if (name === 'TableBody') body = child;
    else rest.push(child);
  });

  return { header, body, rest };
}

const Table = React.forwardRef(({ className, children, toolbar = null, ...props }, ref) => {
  const stickyCols = typeof className === 'string' && className.includes('clr-table--sticky-cols');
  const frameRef = React.useRef(null);
  const headerScrollRef = React.useRef(null);
  const bodyScrollRef = React.useRef(null);
  const headerTableRef = React.useRef(null);
  const bodyTableRef = React.useRef(null);
  const syncingScroll = React.useRef(false);
  const [colWidths, setColWidths] = React.useState([]);

  const setBodyTableRef = (node) => {
    bodyTableRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const { header, body, rest } = React.useMemo(() => splitTableSections(children), [children]);

  const syncScrollFrom = (source) => {
    if (syncingScroll.current) return;
    const headerEl = headerScrollRef.current;
    const bodyEl = bodyScrollRef.current;
    if (!headerEl || !bodyEl) return;
    syncingScroll.current = true;
    if (source === 'body') headerEl.scrollLeft = bodyEl.scrollLeft;
    else bodyEl.scrollLeft = headerEl.scrollLeft;
    syncingScroll.current = false;
  };

  React.useLayoutEffect(() => {
    if (!stickyCols) return undefined;
    const frame = frameRef.current;
    const headerTable = headerTableRef.current;
    const bodyTable = bodyTableRef.current;
    if (!frame || !headerTable) return undefined;

    const measure = () => {
      const headCells = headerTable.querySelectorAll('thead th');
      const bodyRow = bodyTable?.querySelector('tbody tr:first-child');
      const bodyCells = bodyRow
        ? Array.from(bodyRow.children).filter((el) => el.tagName === 'TD')
        : [];
      const count = headCells.length;
      if (!count) return;

      const bodyHasColspan = bodyCells.length > 0 && bodyCells.length !== count;
      const next = [];
      for (let i = 0; i < count; i += 1) {
        const headWidth = headCells[i]?.getBoundingClientRect().width || 0;
        const bodyWidth = bodyHasColspan ? 0 : (bodyCells[i]?.getBoundingClientRect().width || 0);
        next.push(Math.max(1, Math.ceil(Math.max(headWidth, bodyWidth))));
      }

      setColWidths((prev) => {
        if (prev.length === next.length && prev.every((value, index) => value === next[index])) {
          return prev;
        }
        return next;
      });

      frame.style.setProperty('--clr-sticky-col-1-width', `${next[0] || 0}px`);
      frame.style.setProperty('--clr-sticky-col-2-width', `${next[1] || 0}px`);
      frame.style.setProperty('--clr-sticky-col-3-width', `${next[2] || 0}px`);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(headerTable);
    if (bodyTable) observer.observe(bodyTable);
    return () => observer.disconnect();
  }, [stickyCols, children]);

  const colgroup = colWidths.length > 0 ? (
    <colgroup>
      {colWidths.map((width, index) => (
        <col key={`clr-col-${index}`} style={{ width: `${width}px`, minWidth: `${width}px` }} />
      ))}
    </colgroup>
  ) : null;

  if (!stickyCols) {
    return (
      <div className="clr-table-wrap">
        <table ref={ref} className={cn('clr-table', className)} {...props}>
          {children}
        </table>
      </div>
    );
  }

  return (
    <div className="clr-table-frame" ref={frameRef}>
      <div className="clr-table-sticky-pin">
        {toolbar}
        <div
          className="clr-table-header-scroll"
          ref={headerScrollRef}
          onScroll={() => syncScrollFrom('header')}
        >
          <table
            ref={headerTableRef}
            className={cn('clr-table', className, 'clr-table--header-only')}
            {...props}
          >
            {colgroup}
            {header}
            {rest}
          </table>
        </div>
      </div>
      <div
        className="clr-table-body-scroll"
        ref={bodyScrollRef}
        onScroll={() => syncScrollFrom('body')}
      >
        <table
          ref={setBodyTableRef}
          className={cn('clr-table', className, 'clr-table--body-only')}
          {...props}
        >
          {colgroup}
          {body}
        </table>
      </div>
    </div>
  );
});
Table.displayName = 'Table';

const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn(className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr ref={ref} className={cn(className)} {...props} />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th ref={ref} className={cn(className)} {...props} />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td ref={ref} className={cn(className)} {...props} />
));
TableCell.displayName = 'TableCell';

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
